// Compact DM status chips shared between the PatientFiles Actions tab and
// the Work Queue task bodies (e.g. StartMonitoringTask).

interface DmLinkStatusChipProps {
    isLoading: boolean;
    dmId: number | null;
}

export function DmLinkStatusChip({ isLoading, dmId }: DmLinkStatusChipProps) {
    if (isLoading) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                Checking…
            </span>
        );
    }
    if (dmId == null) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                Not linked
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-600">
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            Linked · DM #{dmId}
        </span>
    );
}

interface DmMonitoringStatusChipProps {
    status: string | null | undefined;
}

/**
 * Compact pill rendering the patient's DM monitoring state. DM's enum is
 * "unstarted" | "started" | "paused" | "stopped" — the local mirror stores
 * them capitalized via `capitalizeFirst` in the backend's syncDmPatientToOdo.
 * Only shown when the patient is actually linked (i.e. `dmId != null`).
 */
export function DmMonitoringStatusChip({ status }: DmMonitoringStatusChipProps) {
    const normalized = (status ?? '').toLowerCase();
    let label: string;
    let cls: string;
    // 'resumed' is Monday-speak for an active monitoring (v1 resume action /
    // Monday->DB sync): treat it exactly like started.
    if (normalized === 'started' || normalized === 'resumed') {
        label = 'Active';
        cls = 'bg-green-50 text-green-700';
    } else if (normalized === 'paused') {
        label = 'Paused';
        cls = 'bg-yellow-50 text-yellow-700';
    } else if (normalized === 'stopped') {
        label = 'Stopped';
        cls = 'bg-red-50 text-red-700';
    } else {
        label = 'Not started';
        cls = 'bg-gray-100 text-gray-500';
    }
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold ${cls}`}
        >
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            Monitoring · {label}
        </span>
    );
}

interface DmAppActivationChipProps {
    status: string | null | undefined;
}

export function DmAppActivationChip({ status }: DmAppActivationChipProps) {
    const isActivated = status === 'Patient App Activated';
    const cls = isActivated ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-800';
    const label = isActivated ? 'Activated' : 'Not activated';
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold ${cls}`}
        >
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            App · {label}
        </span>
    );
}
